import type { Connection } from '../../../types/electron';
import type { ActiveConnection } from '../models/connectionTypes';
import { useConnectionStore } from '../store/connectionStore';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

/**
 * Connection lookup result containing both saved and active connection data.
 * In browser mode, only activeConnection may be present.
 */
export interface ConnectionLookupResult {
  /** The saved connection configuration (may be undefined in browser mode) */
  savedConnection: Connection | undefined;
  /** The active connection state (undefined if not currently connected) */
  activeConnection: ActiveConnection | undefined;
  /** The connection configuration - either from saved or active connection */
  connection: Connection | undefined;
  /** The database engine type */
  engine: string;
}

/**
 * Look up a connection by ID, checking both saved connections and active connections.
 *
 * In browser mode (SQLite), connections may only exist in activeConnections.
 * This utility provides a unified way to access connection data from either source.
 *
 * @param connectionId - The connection ID to look up
 * @returns ConnectionLookupResult with connection data and engine type
 */
export function getConnectionById(connectionId: string): ConnectionLookupResult {
  const state = useConnectionStore.getState();

  const savedConnection = state.connections.find((c) => c.id === connectionId);
  const activeConnection = state.activeConnections.find((c) => c.id === connectionId);

  // Connection config from either saved connection or active connection
  const connection = savedConnection ?? activeConnection?.connection;

  // Engine from saved connection, falling back to active connection, then default to 'mssql'
  const engine = savedConnection?.engine ?? activeConnection?.connection.engine ?? 'mssql';

  return {
    savedConnection,
    activeConnection,
    connection,
    engine,
  };
}

/**
 * Get the engine type for a connection ID.
 * Checks both saved connections and active connections.
 *
 * @param connectionId - The connection ID to look up
 * @returns The engine type string (defaults to 'mssql' if not found)
 */
export function getEngineForConnection(connectionId: string): string {
  return getConnectionById(connectionId).engine;
}

/**
 * The engine the store has RECORDED for a connection — saved connection
 * first, then the active one — or `undefined`. Unlike
 * `getEngineForConnection`, never the 'mssql' default: that is a guess, and
 * the wrong-engine schema-load checks must never skip a load on a guess.
 */
export function getRecordedConnectionEngine(connectionId: string): string | undefined {
  const { savedConnection, activeConnection } = getConnectionById(connectionId);
  return savedConnection?.engine ?? activeConnection?.connection.engine;
}

/**
 * Is `loaderEngine` foreign to a connection whose engine is
 * `connectionEngine` — i.e. must a schema loader for `loaderEngine` not touch
 * it? The rule behind `connectionEngineGuard.ts` and the visual query editor's
 * live schema pull.
 *
 * - An unknown connection engine contradicts nothing (a connection the store
 *   has not loaded yet, a synthetic id in a test or benchmark): not foreign.
 * - `mock` is foreign to no loader. The test-only mock engine answers each
 *   dialect's catalog queries by pattern and is deliberately driven through
 *   other engines' loaders (the visual query editor parses a mock tab as
 *   PostgreSQL).
 */
export function isForeignEngine(connectionEngine: string | undefined, loaderEngine: string): boolean {
  if (!connectionEngine || connectionEngine === 'mock') return false;
  return connectionEngine !== loaderEngine;
}

/**
 * Check if a connection exists and is currently active.
 *
 * @param connectionId - The connection ID to check
 * @returns true if the connection exists in activeConnections with 'connected' status
 */
export function isConnectionActive(connectionId: string): boolean {
  const { activeConnection } = getConnectionById(connectionId);
  return activeConnection?.status === 'connected';
}

/**
 * Get connection name/alias for display purposes.
 *
 * @param connectionId - The connection ID to look up
 * @returns The connection alias, server name, or 'Unknown Connection'
 */
export function getConnectionDisplayName(connectionId: string): string {
  const { connection } = getConnectionById(connectionId);
  if (!connection) return 'Unknown Connection';
  return safeConnectionName(connection, 'Unnamed Connection');
}
